import { AfterRenderRef, Component, ComponentRef, DestroyRef, Injector, OnDestroy, OnInit, ViewChild, ViewContainerRef, afterNextRender, effect, inject } from '@angular/core';
import { MemberDetailsComponent, MemberStorePayload } from './member-details.component';
import { IMemberState } from '../store/member/state';
import { getAddMemberFeed, getDependentInfo, getSelectedSubscriber } from '../store/member/selector';
import { PreviousRouteService } from '../shared/services/previousUrl.service';
import { getSelectedAdminPlan } from '../login/store/selectors';
import { ILoginState } from '../login/store/state';
import moment from 'moment';
import { IAppAuthorizations, IUiAuthorizations } from 'src/app/authorizations/state';
import { EligUiAuthSerive } from '@roosevelt/elig-ui-lib';
import { ClientUiAuthSerive } from '@roosevelt/client-ui-lib';
import { getStrictUiAuth } from 'src/app/authorizations/selector';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { outputToObservable, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

type MemberFeed = IMemberState['addMemberFeed'];

type SelectedSubscriber = {
  name?: {
    first?: string;
    last?: string;
  };
  firstName?: string;
  lastName?: string;
  planAcronym?: string | string[] | [];
  clientSpecifiedIdentifier?: string;
  subclientSpecifiedIdentifier?: string;
  subscriberEnrollmentId?: string | number;
  subscriberEnrollmentID?: string | number;
  eligibilityEffectiveDate?: string;
  asOf?: string | null;
};

type DependentInfo = {
  dependentAdded: boolean;
  value: Record<string, unknown>;
};

type ResolvedClientInfo = {
  planAcronym: string;
  clientSpecifiedId: string;
  subClientSpecifiedId: string;
  asOf?: string | null;
};

@Component({
  selector: 'app-member-details',
  template: '<ng-container #memberDetailsComponent></ng-container>',
  standalone: false
})
export class MemberDetailsContainer implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private pendingAddFlowNavigationRef: AfterRenderRef | null = null;
  readonly luxId = 'BMT-MemberDetailsContainer-' + this.constructor.name;
  @ViewChild('memberDetailsComponent', { read: ViewContainerRef, static: true })
  componentArea!: ViewContainerRef;

  componentRef: ComponentRef<MemberDetailsComponent> | null = null;
  componentInstance: MemberDetailsComponent | null = null;
  readonly selectedMember = toSignal(this.lux.get(getSelectedSubscriber, this.luxId), { initialValue: null as SelectedSubscriber | null });
  readonly getDependentInfo = toSignal(this.lux.get(getDependentInfo, this.luxId), { initialValue: { dependentAdded: false, value: {} } });
  readonly getAddMemberFeed = toSignal(this.lux.get(getAddMemberFeed, this.luxId), { initialValue: { planAcronym: '', clientId: '', subClientId: '', asOf: '' } });
  readonly selectedAdminPlan = toSignal(this.lux.get(getSelectedAdminPlan, this.luxId), { initialValue: '' });
  readonly strictAuth = toSignal(this.lux.get(getStrictUiAuth, this.luxId), { initialValue: {} as IUiAuthorizations | {} });

  constructor(
    private readonly clientUiAuth: ClientUiAuthSerive,
    private readonly eligUiAuth: EligUiAuthSerive,
    private readonly routerService: PreviousRouteService,
    private readonly route: Router,
    private readonly lux: Lux<{ member: IMemberState, login: ILoginState, appUiAuth: IAppAuthorizations }>
  ) {
    effect(() => {
      const uiAuth = this.strictAuth() as IUiAuthorizations;
      if (uiAuth?.eligibility) {
        this.eligUiAuth.setStrictAuthorizations(uiAuth.eligibility);
      }
      if (uiAuth?.client) {
        this.clientUiAuth.setStrictAuthorizations(uiAuth.client);
      }
    });

    effect(() => {
      this.syncMemberDetailsInputs();
    });

    effect(() => {
      const adminPlan = this.selectedAdminPlan();
      if (this.componentRef) {
        this.componentRef.setInput('selectedAdminPlan', adminPlan);
      }
    });
  }

  ngOnInit(): void {
    this._loadComponent();
  }

  private _loadComponent(): void {
    this.componentArea.clear();
    this.componentRef = this.componentArea.createComponent(MemberDetailsComponent);
    this.componentInstance = this.componentRef.instance;
    outputToObservable(this.componentInstance.setMemberStore)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((memberState: MemberStorePayload) => {
        const stateEvent = memberState.event;
        const stateWithoutEvent = { ...memberState };
        delete stateWithoutEvent.event;
        this.lux.set(state => state.member, { ...stateWithoutEvent } as unknown as Partial<IMemberState>);
        if (stateEvent === 'add flow') {
          this.scheduleAddFlowNavigation();
        }
      });
    this.componentRef.setInput('selectedAdminPlan', this.selectedAdminPlan());
    this.syncMemberDetailsInputs();
  }

  private scheduleAddFlowNavigation(): void {
    this.pendingAddFlowNavigationRef?.destroy();
    this.pendingAddFlowNavigationRef = afterNextRender(() => {
      this.pendingAddFlowNavigationRef = null;
      void this.route.navigate(['/add-member']);
    }, { injector: this.injector });
  }

  private resolvePlanAcronym(planAcronym: string | string[] | undefined): string {
    return Array.isArray(planAcronym)
      ? (planAcronym[0] ?? '')
      : (planAcronym ?? '');
  }

  private resolveSubscriberAsOf(selectedSubscriber: SelectedSubscriber): string | null | undefined {
    const currentAsOf = selectedSubscriber.asOf;
    const eligibilityEffectiveDate = selectedSubscriber.eligibilityEffectiveDate;
    return eligibilityEffectiveDate && moment(eligibilityEffectiveDate).isAfter(moment(currentAsOf || new Date()).format('YYYY-MM-DD'))
      ? eligibilityEffectiveDate
      : currentAsOf;
  }

  private hasSelectedMemberData(selectedMember: SelectedSubscriber | null): selectedMember is SelectedSubscriber {
    return Boolean(selectedMember && Object.keys(selectedMember).length);
  }

  private syncMemberDetailsInputs(): void {
    const selectedMember = this.selectedMember() as SelectedSubscriber | null;
    const addMemberFeed = this.getAddMemberFeed() as MemberFeed;
    const dependentInfo = this.getDependentInfo() as DependentInfo;

    if (!this.componentRef || !this.hasSelectedMemberData(selectedMember)) {
      return;
    }

    const routeState = this.resolveRouteState(selectedMember, addMemberFeed);
    const resolvedAsOf = this.resolveAsOf(
      selectedMember,
      addMemberFeed,
      routeState.resolvedClientInfo,
      routeState.fromEligRoute
    );
    const resolvedEnrollmentId = this.resolveEnrollmentId(
      selectedMember,
      addMemberFeed,
      routeState.resolvedClientInfo,
      routeState.fromEligRoute
    );

    this.componentRef.setInput('showBackToResults', routeState.showBackToResults);
    this.componentRef.setInput('initialClientSelected', {
      ...this.buildClientSelection(
        selectedMember,
        addMemberFeed,
        routeState.resolvedClientInfo,
        routeState.fromEligRoute,
        resolvedAsOf
      ),
      asOf: resolvedAsOf || undefined
    });
    this.componentRef.setInput('subscriberFeed', resolvedEnrollmentId ? {
      enrollmentId: Number(resolvedEnrollmentId),
      asOf: resolvedAsOf
    } : undefined);
    this.componentRef.setInput('initialMemberSelected', dependentInfo?.dependentAdded
      ? dependentInfo.value
      : {
          name: {
            first: selectedMember.firstName ?? selectedMember.name?.first,
            last: selectedMember.lastName ?? selectedMember.name?.last
          }
        });
  }

  private resolveRouteState(selectedMember: SelectedSubscriber, addMemberFeed: MemberFeed): {
    fromEligRoute: boolean;
    showBackToResults: boolean;
    resolvedClientInfo: ResolvedClientInfo | null;
  } {
    const previousUrl = this.routerService.getPreviousUrl() || '/index';
    const fromEligRoute = !this.isAddOrFamilyRoute(previousUrl);
    const isPageRefreshed = previousUrl === '/details' || previousUrl === '/index';

    return {
      fromEligRoute,
      showBackToResults: this.resolveShowBackToResults(isPageRefreshed, fromEligRoute, selectedMember, addMemberFeed),
      resolvedClientInfo: isPageRefreshed ? this.buildRefreshedClientInfo(selectedMember, addMemberFeed) : null
    };
  }

  private isAddOrFamilyRoute(previousUrl: string): boolean {
    return previousUrl.includes('/add-member') || previousUrl.includes('/family-enrollment');
  }

  private resolveShowBackToResults(
    isPageRefreshed: boolean,
    fromEligRoute: boolean,
    selectedMember: SelectedSubscriber,
    addMemberFeed: MemberFeed
  ): boolean {
    if (isPageRefreshed) {
      return !!(this.resolvePlanAcronym(selectedMember.planAcronym) || this.resolvePlanAcronym(addMemberFeed?.planAcronym));
    }

    return fromEligRoute;
  }

  private buildRefreshedClientInfo(selectedMember: SelectedSubscriber, addMemberFeed: MemberFeed): ResolvedClientInfo {
    return {
      planAcronym: this.resolvePlanAcronym(selectedMember.planAcronym) || this.resolvePlanAcronym(addMemberFeed?.planAcronym),
      clientSpecifiedId: selectedMember.clientSpecifiedIdentifier || addMemberFeed?.clientId || '',
      subClientSpecifiedId: selectedMember.subclientSpecifiedIdentifier || addMemberFeed?.subClientId || '',
      asOf: selectedMember.asOf || addMemberFeed?.asOf
    };
  }

  private resolveAsOf(
    selectedMember: SelectedSubscriber,
    addMemberFeed: MemberFeed,
    resolvedClientInfo: ResolvedClientInfo | null,
    fromEligRoute: boolean
  ): string | null | undefined {
    if (resolvedClientInfo) {
      return resolvedClientInfo.asOf;
    }

    if (!fromEligRoute) {
      return addMemberFeed?.asOf;
    }

    return this.resolveSubscriberAsOf(selectedMember);
  }

  private resolveEnrollmentId(
    selectedMember: SelectedSubscriber,
    addMemberFeed: MemberFeed,
    resolvedClientInfo: ResolvedClientInfo | null,
    fromEligRoute: boolean
  ): string | number | undefined {
    if (resolvedClientInfo) {
      return selectedMember.subscriberEnrollmentId ?? selectedMember.subscriberEnrollmentID ?? addMemberFeed?.subscriberEnrollmentID;
    }

    if (!fromEligRoute) {
      return addMemberFeed?.subscriberEnrollmentID ?? selectedMember.subscriberEnrollmentId ?? selectedMember.subscriberEnrollmentID;
    }

    return selectedMember.subscriberEnrollmentId ?? selectedMember.subscriberEnrollmentID ?? addMemberFeed?.subscriberEnrollmentID;
  }

  private buildClientSelection(
    selectedMember: SelectedSubscriber,
    addMemberFeed: MemberFeed,
    resolvedClientInfo: ResolvedClientInfo | null,
    fromEligRoute: boolean,
    resolvedAsOf: string | null | undefined
  ): ResolvedClientInfo {
    const clientInfo = resolvedClientInfo ?? this.buildRouteClientInfo(selectedMember, addMemberFeed, fromEligRoute);

    return {
      planAcronym: this.resolvePlanAcronym(clientInfo.planAcronym),
      clientSpecifiedId: clientInfo.clientSpecifiedId || '',
      subClientSpecifiedId: clientInfo.subClientSpecifiedId || '',
      asOf: resolvedAsOf
    };
  }

  private buildRouteClientInfo(
    selectedMember: SelectedSubscriber,
    addMemberFeed: MemberFeed,
    fromEligRoute: boolean
  ): ResolvedClientInfo {
    if (!fromEligRoute) {
      return {
        planAcronym: this.resolvePlanAcronym(addMemberFeed?.planAcronym),
        clientSpecifiedId: addMemberFeed?.clientId || '',
        subClientSpecifiedId: addMemberFeed?.subClientId || '',
        asOf: addMemberFeed?.asOf
      };
    }

    return {
      planAcronym: this.resolvePlanAcronym(selectedMember.planAcronym),
      clientSpecifiedId: selectedMember.clientSpecifiedIdentifier || '',
      subClientSpecifiedId: selectedMember.subclientSpecifiedIdentifier || '',
      asOf: selectedMember.asOf
    };
  }

  ngOnDestroy(): void {
    this.pendingAddFlowNavigationRef?.destroy();
    this.pendingAddFlowNavigationRef = null;
    this.componentRef?.destroy();
    this.componentRef = null;
    this.componentInstance = null;
    this.lux.set(state => state.member, { selectedDependent: {}, dependentAdded: false });
    this.lux.destroy(this.luxId);
  }
}
